import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";

import {
  ExpressionSpecification,
  GeoJSONSource,
  Map as MaplibreMap
} from "maplibre-gl";
import {
  Feature,
  FeatureCollection,
  GeoJsonProperties,
  Geometry,
  LineString,
  Polygon,
  Position
} from "geojson";

import { useGetSettings } from "@/api/configuration/useConfigurationAPI";
import {
  CATCHMENT_DATASET_POLYGON_FILL_COLOR,
  CATCHMENT_DATASET_POLYGON_FILL_OPACITY,
  CATCHMENT_DATASET_POLYGON_SOURCE_ID,
  CATCHMENT_DATASET_POLYGON_STROKE_COLOR,
  CATCHMENT_DATASET_POLYGON_STROKE_SELECTED_COLOR,
  CATCHMENT_DATASET_POLYGON_STROKE_SELECTED_WIDTH,
  CATCHMENT_DATASET_POLYGON_STROKE_WIDTH,
  CIRCE_RADIUS_SOURCE_ID,
  ROUTING_SOURCE_ID,
  UNIQUE_ID_PROPERTY
} from "@/constants/map";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { RootState } from "@/store";
import { Dataset } from "@/types/dataset";
import { MapStyleProperties, MarkerPinItem } from "@/types/map";
import {
  AreaInputType,
  CatchmentDataset,
  CatchmentInputBy,
  ECatchmentInputBy,
  ECatchmentType
} from "@/types/spatial";
import {
  extractCoordinatesFromDatasets,
  getRenderableCatchmentDatasets
} from "@/utils/catchment";
import { convertDatasetResponseToFeatureCollection } from "@/utils/dataset";
import {
  addPolygonLayer,
  addRoutingLayer,
  removeAllSourceLayerWithPrefix
} from "@/utils/map";
import { CATCHMENT_MARKER_ICON_LAYER_ID } from "@/utils/map/catchmentMarker";
import {
  addCentroidSource,
  addPolygonCatchmentSource
} from "@/utils/map/polygon";
import { mergeRankToFeatures } from "@/utils/map/rank";

import { circle, lineString, lineToPolygon } from "@turf/turf";

type UseCatchmentAreaProps = {
  map: MaplibreMap;
  isMapLoaded: boolean;
  removeAllMarkers: () => void;
  handleAddPolygonLayer: (
    dataset: Dataset,
    polygonData: FeatureCollection<Geometry>,
    customId?: string
  ) => void;
  handleAddMarkers: (
    data: { name?: string; coordinates: [number, number] | Position }[]
  ) => void;
};

const isValidLineString = (geometry: Geometry | undefined): boolean => {
  return !!(
    geometry &&
    geometry?.type === "LineString" &&
    geometry?.coordinates.length >= 2
  );
};

/**
 * Builds one FeatureCollection of every radius circle (all markers × all radius),
 * drawn as a single GeoJSON source + fill/line layer pair. Each circle carries
 * `markerId`/`radius` properties for identification.
 */
const buildRadiusCirclesCollection = (
  coordinates: MarkerPinItem[],
  radiusData: string[]
): FeatureCollection<Polygon, GeoJsonProperties> => {
  const validRadius = (radiusData ?? []).filter(
    (radius) => typeof radius === "string" && radius.trim() !== ""
  );

  const features: Feature<Polygon, GeoJsonProperties>[] = [];

  for (const item of coordinates) {
    const center = [item.lng, item.lat];
    for (const radius of validRadius) {
      const meters = Number.parseInt(radius);
      if (Number.isNaN(meters)) continue;

      const circleArea = circle(center, meters, {
        steps: 64,
        units: "meters"
      });
      circleArea.properties = { markerId: item.id, radius };
      features.push(circleArea);
    }
  }

  return { type: "FeatureCollection", features };
};

/**
 * Signature of the inputs that define the circles (centers + radii). Lets us
 * skip a redundant setData when an effect re-runs without changing geometry.
 */
const circleRadiusSignature = (
  coordinates: MarkerPinItem[],
  radiusData: string[]
): string =>
  JSON.stringify({
    c: coordinates.map((item) => [item.id, item.lng, item.lat]),
    r: radiusData
  });

export const useCatchmentArea = ({
  map,
  isMapLoaded,
  removeAllMarkers,
  handleAddPolygonLayer,
  handleAddMarkers
}: UseCatchmentAreaProps) => {
  const [searchParams] = useSearchParams();
  const isPreviewMode = searchParams.get("preview") === "true";
  const {
    catchmentSettings,
    spatialConfig: { areaInput: spatialAreaInputType },
    catchmentSettingsTemp
  } = useSelector((state: RootState) => state.spatial);

  const {
    showCatchmentPopup,
    showAnalysisResultPopup,
    showComparisonPopup,
    showAnalysisSummaryPopup,
    showSearchLocationPopup,
    isDrawPolygon
  } = useSelector((state: RootState) => state.ui);
  const { isBVTDataForCatchmentEnabled, isRankingLabelEnabled } =
    useAppFeatures();
  const { data: analysisSettings } = useGetSettings();

  // Signature of the last radius-circle geometry drawn (see circleRadiusSignature).
  const lastCircleSignature = useRef<string | null>(null);

  /** Polygon catchment layers live only inside the BVT multi-dataset flow
   * AND only while the user picked BY_DATASET as the catchment input mode.
   * Keep the input-mode check explicit so that, when the FF is eventually
   * cleaned up, the layer lifecycle stays scoped to BY_DATASET and doesn't
   * bleed into BY_LOCATION / other modes.
   */
  const isPolygonDatasetCatchment =
    isBVTDataForCatchmentEnabled &&
    catchmentSettings?.catchmentInputBy === ECatchmentInputBy.BY_DATASET;

  const handleAddCatchmentLayer = async (
    dataset: Dataset,
    polygonData: FeatureCollection<Geometry>
  ) => {
    if (!polygonData || !map) return;

    removeAllMarkers();

    const polygonDataCatchment = {
      type: "FeatureCollection",
      features: polygonData.features.map((feature) => ({
        ...feature,
        geometry: feature.properties?.catchment_geometry,
        properties: {
          ...Object.fromEntries(
            Object.entries(feature.properties || {}).filter(
              ([key]) =>
                !["catchment_geometry", "location_geometry"].includes(key)
            )
          )
        }
      }))
    } as FeatureCollection<Geometry>;

    handleAddPolygonLayer(dataset, polygonDataCatchment);
    handleAddMarkers(
      polygonData.features.map((feature) => ({
        coordinates: feature.properties?.location_geometry
          .coordinates as unknown as [number, number]
      }))
    );
  };

  const handleAddRegionLayer = (
    sourceId: string,
    polygonData:
      | FeatureCollection<Geometry>
      | Feature<Polygon, GeoJsonProperties>,
    styleProperties?: MapStyleProperties
  ) => {
    if (!map || !isMapLoaded) return;
    addPolygonCatchmentSource(map, sourceId, polygonData);
    addPolygonLayer(map, sourceId, styleProperties);
  };

  /**
   * Renders polygon-typed catchment datasets as fill + border layers plus an
   * optional centroid label/rank symbol layer. Wipes all previous
   * CATCHMENT_DATASET_POLYGON_SOURCE_ID-* layers first, then re-adds the visible
   * datasets.
   */
  const renderPolygonCatchmentDatasets = (
    datasets: Record<string, CatchmentDataset>,
    visualizationOrder: string[] = []
  ) => {
    if (!map || !isMapLoaded) return;
    /** Wipe previous polygon catchment layers unconditionally (before the
     * early-return below), so they're removed the moment showAnalysisResultPopup
     * toggles on and stale labels never outlive their dataset.
     */
    removeAllSourceLayerWithPrefix(
      map,
      CATCHMENT_DATASET_POLYGON_SOURCE_ID,
      []
    );
    /** Catchment input layers are pre-analysis decoration and must not bleed
     * through analysis output or the compared dataset. The committed-state
     * effect re-runs when either view closes and re-adds them.
     */
    if (showAnalysisResultPopup || showComparisonPopup.isShow) return;
    const visibleDatasetIds = new Set(
      getRenderableCatchmentDatasets(
        datasets,
        visualizationOrder,
        analysisSettings?.max_catchments
      ).map((entry) => entry.dataset.data.id)
    );
    for (const entry of Object.values(datasets)) {
      const { dataset, rawDataset } = entry;
      if (dataset.data.spatial_aggregation_type !== "polygon") continue;
      if (!visibleDatasetIds.has(dataset.data.id)) continue;

      const labelAttributeName =
        dataset.style?.labelConfig?.attribute_name ?? "";
      // Skip the label when attribute_name is empty — it would render nothing.
      const showPolygonLabel =
        dataset.style?.labelConfig?.show_label !== false &&
        labelAttributeName !== "";
      const showRankLabel =
        isRankingLabelEnabled && !!dataset.style?.rankConfig?.isEnabled;

      /** Pass the schema (`dataset.categories`) so feature property keys are the
       * `attribute_name`, letting label and rank read by
       * `labelConfig.attribute_name`. Relies on categories being populated;
       * without it keys fall back to raw column names and label/rank resolve empty.
       */
      const baseFeatureCollection = convertDatasetResponseToFeatureCollection(
        rawDataset,
        dataset.categories
      );

      /** mergeRankToFeatures keys ranks by `unique_id`, but the conversion only
       * sets `feature.id` — stamp a stable `unique_id` here, else every feature
       * collides on `undefined` and shares one rank.
       */
      const featureCollection: FeatureCollection<Geometry> = {
        ...baseFeatureCollection,
        features: baseFeatureCollection.features.map((feature, index) => ({
          ...feature,
          properties: {
            ...feature.properties,
            [UNIQUE_ID_PROPERTY]:
              feature.properties?.[UNIQUE_ID_PROPERTY] ?? index
          }
        }))
      };

      const sourceId = `${CATCHMENT_DATASET_POLYGON_SOURCE_ID}-${dataset.data.id}`;
      handleAddRegionLayer(sourceId, featureCollection, {
        fillColor: CATCHMENT_DATASET_POLYGON_FILL_COLOR,
        fillOpacity: CATCHMENT_DATASET_POLYGON_FILL_OPACITY
      });

      /** Border stroke matching a drawn polygon, thicker/highlighted on
       * feature-state `selected`. Shares the source prefix so the wipe above
       * removes it too.
       */
      if (!map.getLayer(`${sourceId}-stroke`)) {
        map.addLayer({
          id: `${sourceId}-stroke`,
          type: "line",
          source: sourceId,
          layout: {
            "line-cap": "round",
            "line-join": "round"
          },
          paint: {
            "line-color": [
              "case",
              ["boolean", ["feature-state", "selected"], false],
              CATCHMENT_DATASET_POLYGON_STROKE_SELECTED_COLOR,
              CATCHMENT_DATASET_POLYGON_STROKE_COLOR
            ],
            "line-width": [
              "case",
              ["boolean", ["feature-state", "selected"], false],
              CATCHMENT_DATASET_POLYGON_STROKE_SELECTED_WIDTH,
              CATCHMENT_DATASET_POLYGON_STROKE_WIDTH
            ]
          }
        });
      }

      // No label and no rank: nothing to put on the centroid source.
      if (!showPolygonLabel && !showRankLabel) continue;

      // Merge ranks onto the features so the label layer can read them.
      // mergeRankToFeatures no-ops when rank is disabled.
      const centroidFeatureCollection: FeatureCollection<Geometry> =
        showRankLabel
          ? {
              ...featureCollection,
              features: mergeRankToFeatures(
                isRankingLabelEnabled,
                featureCollection.features,
                dataset.style
              )
            }
          : featureCollection;

      addCentroidSource(map, `${sourceId}-centroid`, centroidFeatureCollection);

      // Label and rank ("#N") render as one multi-line symbol so they never
      // collide and declutter together on zoom-out.
      const labelExpr: ExpressionSpecification = [
        "to-string",
        ["get", labelAttributeName]
      ];
      const rankExpr: ExpressionSpecification = ["to-string", ["get", "rank"]];
      let textField: ExpressionSpecification;
      if (showPolygonLabel && showRankLabel) {
        // Ranked features show "rank\nlabel"; unranked ones show just label.
        textField = [
          "case",
          ["has", "rank"],
          ["concat", rankExpr, "\n", labelExpr],
          labelExpr
        ];
      } else if (showPolygonLabel) {
        textField = labelExpr;
      } else {
        // Rank-only: blank when a feature has no rank so nothing is stamped.
        textField = ["case", ["has", "rank"], rankExpr, ""];
      }

      if (!map.getLayer(`${sourceId}-label`)) {
        map.addLayer({
          id: `${sourceId}-label`,
          type: "symbol",
          source: `${sourceId}-centroid`,
          layout: {
            "symbol-placement": "point",
            "text-field": textField,
            "text-font": ["Open Sans Bold", "Arial Unicode MS Regular"],
            "text-size": 12,
            "text-anchor": "center",
            "text-allow-overlap": false,
            "text-pitch-alignment": "viewport",
            "text-rotation-alignment": "viewport"
          },
          paint: {
            "text-color": "#000000",
            "text-halo-color": "#FFFFFF",
            "text-halo-width": 1.2
          }
        });
      }
    }
  };

  // TODO: @vudh15 refactor for better naming
  const handleAddIsochroneLayer = (
    sourceId: string,
    polygonData:
      | FeatureCollection<Geometry>
      | Feature<Polygon, GeoJsonProperties>,
    styleProperties?: MapStyleProperties
  ) => {
    if (!map || !isMapLoaded) return;
    addPolygonCatchmentSource(map, sourceId, polygonData);
    // Insert below the catchment marker layers so the circle stays underneath the marker.
    addRoutingLayer(
      map,
      sourceId,
      styleProperties,
      CATCHMENT_MARKER_ICON_LAYER_ID
    );
  };

  const removeCatchmentMapDisplay = () => {
    removeAllMarkers();
    // Remove all region polygon
    removeAllSourceLayerWithPrefix(map, CIRCE_RADIUS_SOURCE_ID, []);
    removeAllSourceLayerWithPrefix(map, ROUTING_SOURCE_ID, []);
    /** Polygon catchment dataset layers must follow the catchment input
     * lifecycle — when the catchment display is being wiped (e.g. switching
     * to admin-area mode or entering preview), wipe these too. Only relevant
     * inside the BVT multi-dataset catchment flow with BY_DATASET input.
     */
    if (isPolygonDatasetCatchment) {
      removeAllSourceLayerWithPrefix(
        map,
        CATCHMENT_DATASET_POLYGON_SOURCE_ID,
        []
      );
    }
  };

  /** Renders the radius circles as a single merged GeoJSON source. */
  const renderCircleRadius = (
    coordinates: MarkerPinItem[],
    radiusData: string[]
  ) => {
    if (!map || !isMapLoaded) return;

    const source = map.getSource<GeoJSONSource>(CIRCE_RADIUS_SOURCE_ID);
    const signature = circleRadiusSignature(coordinates, radiusData);

    // Unchanged and already drawn — nothing to do.
    if (source && lastCircleSignature.current === signature) return;

    const radiusCircleCollection = buildRadiusCirclesCollection(
      coordinates,
      radiusData
    );

    if (source) {
      source.setData(radiusCircleCollection);
    } else if (radiusCircleCollection.features.length > 0) {
      handleAddIsochroneLayer(CIRCE_RADIUS_SOURCE_ID, radiusCircleCollection);
    } else {
      // No source and nothing to draw — leave the signature unset.
      return;
    }

    lastCircleSignature.current = signature;
  };

  /**
   * Removes the radius-circle source/layers when leaving BY_RADIUS mode.
   */
  const clearCircleRadius = () => {
    if (!map) return;
    removeAllSourceLayerWithPrefix(map, CIRCE_RADIUS_SOURCE_ID, []);
    lastCircleSignature.current = null;
  };

  const handleAddCircleRadiusOriginal = (
    catchmentInputBy?: CatchmentInputBy
  ) => {
    const radiusData = catchmentSettings?.radiusData ?? [];
    const catchmentType = catchmentSettings?.catchmentType;
    const isCatchment = spatialAreaInputType?.value === AreaInputType.CATCHMENT;

    if (
      !isMapLoaded ||
      !map ||
      !isCatchment ||
      showCatchmentPopup ||
      showAnalysisResultPopup ||
      showComparisonPopup.isShow ||
      showAnalysisSummaryPopup ||
      showSearchLocationPopup ||
      isPreviewMode
    )
      return;

    // Clear any previous routing isochrone layers before drawing the radius circles.
    removeAllSourceLayerWithPrefix(map, ROUTING_SOURCE_ID, []);

    if (catchmentType !== ECatchmentType.BY_RADIUS) {
      clearCircleRadius();
      return;
    }

    const coordinates =
      catchmentInputBy === ECatchmentInputBy.BY_LOCATION
        ? (catchmentSettings?.placeCoordinates ?? [])
        : extractCoordinatesFromDatasets(
            Object.values(catchmentSettings?.datasetConfig?.datasets ?? {})
          );

    renderCircleRadius(coordinates, radiusData);
  };

  const handleAddCircleRadiusTemp = (catchmentInputBy?: CatchmentInputBy) => {
    const radiusDataTemp = catchmentSettingsTemp?.radiusData ?? [];
    const catchmentTypeTemp = catchmentSettingsTemp.catchmentType;
    const isCatchment = spatialAreaInputType?.value === AreaInputType.CATCHMENT;

    if (!isMapLoaded || !map || !isCatchment || !showCatchmentPopup) return;

    if (catchmentTypeTemp !== ECatchmentType.BY_RADIUS) {
      clearCircleRadius();
      return;
    }

    const coordinatesTemp =
      catchmentInputBy === ECatchmentInputBy.BY_LOCATION
        ? (catchmentSettingsTemp.placeCoordinates ?? [])
        : extractCoordinatesFromDatasets(
            getRenderableCatchmentDatasets(
              catchmentSettingsTemp.datasetConfig?.datasets ?? {},
              catchmentSettingsTemp.datasetConfig?.visualizationOrder,
              analysisSettings?.max_catchments
            )
          );

    renderCircleRadius(coordinatesTemp, radiusDataTemp);
  };

  const handleAddIsochrone = () => {
    const catchmentType = catchmentSettings?.catchmentType;
    const isCatchment = spatialAreaInputType?.value === AreaInputType.CATCHMENT;
    const isochroneData =
      catchmentSettings?.roadAccessConfig?.isochroneData ?? [];
    const isochroneDataMapping = isochroneData.map((item) => item?.data).flat();

    if (
      !isMapLoaded ||
      !map ||
      !isCatchment ||
      catchmentType === ECatchmentType.BY_RADIUS ||
      showCatchmentPopup ||
      showAnalysisResultPopup ||
      showComparisonPopup.isShow ||
      showAnalysisSummaryPopup ||
      showSearchLocationPopup ||
      isPreviewMode
    )
      return;

    removeAllSourceLayerWithPrefix(map, ROUTING_SOURCE_ID, []);

    const isochroneDataValid = isochroneDataMapping
      .map((item) => item?.features)
      .flat();

    isochroneDataValid.forEach((item, index) => {
      if (isValidLineString(item.geometry)) {
        const line = lineString((item.geometry as LineString).coordinates);
        const polygon = lineToPolygon(line);
        handleAddIsochroneLayer(`${ROUTING_SOURCE_ID}-${index}`, {
          type: "FeatureCollection",
          features: [polygon]
        });
      }
    });
  };

  const handleAddIsochroneTemp = () => {
    const catchmentTypeTemp = catchmentSettingsTemp.catchmentType;
    const isCatchment = spatialAreaInputType?.value === AreaInputType.CATCHMENT;
    const isochroneData =
      catchmentSettingsTemp.roadAccessConfig?.isochroneData ?? [];

    const isochroneDataMapping = isochroneData
      .filter((item) => item.data)
      .map((item) => item.data)
      .flat();

    if (
      !isMapLoaded ||
      !map ||
      !isCatchment ||
      catchmentTypeTemp === ECatchmentType.BY_RADIUS ||
      !showCatchmentPopup
    )
      return;
    removeAllSourceLayerWithPrefix(map, ROUTING_SOURCE_ID, []);

    const isochroneDataValid = isochroneDataMapping
      .map((item) => item?.features)
      .flat();

    isochroneDataValid.forEach((item, index) => {
      if (isValidLineString(item.geometry)) {
        const line = lineString((item.geometry as LineString).coordinates);
        const polygon = lineToPolygon(line);
        handleAddIsochroneLayer(`${ROUTING_SOURCE_ID}-${index}`, {
          type: "FeatureCollection",
          features: [polygon]
        });
      }
    });
  };

  useEffect(() => {
    const catchmentType = catchmentSettings?.catchmentType;
    const catchmentTypeTemp = catchmentSettingsTemp.catchmentType;
    if (showCatchmentPopup) {
      removeAllSourceLayerWithPrefix(
        map,
        catchmentTypeTemp === ECatchmentType.BY_RADIUS
          ? ROUTING_SOURCE_ID
          : CIRCE_RADIUS_SOURCE_ID,
        []
      );
    } else {
      removeAllSourceLayerWithPrefix(
        map,
        catchmentType === ECatchmentType.BY_RADIUS
          ? ROUTING_SOURCE_ID
          : CIRCE_RADIUS_SOURCE_ID,
        []
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isMapLoaded,
    showCatchmentPopup,
    catchmentSettings?.catchmentType,
    catchmentSettingsTemp.catchmentType
  ]);

  // Syncs the COMMITTED catchment state (catchmentSettings) to map layers.
  // Active when the catchment popup is closed. Dispatches to exactly one
  // handler based on catchmentType. When polygon-draw mode is on (BVT flow),
  // the polygon flow owns the visualization, so both source prefixes are wiped.
  useEffect(() => {
    if (isBVTDataForCatchmentEnabled && isDrawPolygon && map) {
      removeAllSourceLayerWithPrefix(map, CIRCE_RADIUS_SOURCE_ID, []);
      removeAllSourceLayerWithPrefix(map, ROUTING_SOURCE_ID, []);
      removeAllSourceLayerWithPrefix(
        map,
        CATCHMENT_DATASET_POLYGON_SOURCE_ID,
        []
      );
      return;
    }
    // Polygon catchment dataset layers belong to the BY_DATASET tab only.
    // When the saved catchmentInputBy is anything else, wipe them so the map
    // matches the active input mode.
    if (isPolygonDatasetCatchment) {
      renderPolygonCatchmentDatasets(
        catchmentSettings?.datasetConfig?.datasets ?? {},
        catchmentSettings?.datasetConfig?.visualizationOrder
      );
    } else if (isBVTDataForCatchmentEnabled) {
      renderPolygonCatchmentDatasets({});
    }
    if (catchmentSettings?.catchmentType === ECatchmentType.BY_RADIUS) {
      handleAddCircleRadiusOriginal(catchmentSettings?.catchmentInputBy);
    } else {
      handleAddIsochrone();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isMapLoaded,
    showCatchmentPopup,
    spatialAreaInputType,
    catchmentSettings?.catchmentInputBy,
    catchmentSettings?.catchmentType,
    catchmentSettings?.radiusData,
    catchmentSettings?.placeCoordinates,
    catchmentSettings?.datasetConfig?.datasets,
    catchmentSettings?.roadAccessConfig?.isochroneData,
    showAnalysisResultPopup,
    showAnalysisSummaryPopup,
    showComparisonPopup,
    showSearchLocationPopup,
    isPreviewMode,
    isDrawPolygon,
    isRankingLabelEnabled,
    analysisSettings?.max_catchments
  ]);

  // Syncs the DRAFT catchment state (catchmentSettingsTemp) to map layers.
  // Active while the catchment popup is open so the user sees a live preview
  // of edits before committing. Same single-handler dispatch by catchmentType
  // as the committed-state effect above.
  useEffect(() => {
    if (isBVTDataForCatchmentEnabled && isDrawPolygon && map) {
      removeAllSourceLayerWithPrefix(map, CIRCE_RADIUS_SOURCE_ID, []);
      removeAllSourceLayerWithPrefix(map, ROUTING_SOURCE_ID, []);
      removeAllSourceLayerWithPrefix(
        map,
        CATCHMENT_DATASET_POLYGON_SOURCE_ID,
        []
      );
      return;
    }
    /** The temp render only owns the preview while the catchment popup is
     * open. After save → onCloseCatchmentPopup clears catchmentSettingsTemp
     * back to initial state and this effect would otherwise wipe the polygon
     * layers that the committed-state effect just put down.
     * Same as the committed effect: polygon dataset layers must follow the
     * active tab. Switching to BY_LOCATION / BY_POLYGON wipes them; switching
     * back to BY_DATASET re-renders them.
     */
    if (isBVTDataForCatchmentEnabled && showCatchmentPopup) {
      renderPolygonCatchmentDatasets(
        catchmentSettingsTemp?.catchmentInputBy === ECatchmentInputBy.BY_DATASET
          ? (catchmentSettingsTemp?.datasetConfig?.datasets ?? {})
          : {},
        catchmentSettingsTemp?.datasetConfig?.visualizationOrder
      );
    }
    if (catchmentSettingsTemp?.catchmentType === ECatchmentType.BY_RADIUS) {
      handleAddCircleRadiusTemp(catchmentSettingsTemp?.catchmentInputBy);
    } else {
      handleAddIsochroneTemp();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isMapLoaded,
    showCatchmentPopup,
    spatialAreaInputType,
    catchmentSettingsTemp?.catchmentInputBy,
    catchmentSettingsTemp?.catchmentType,
    catchmentSettingsTemp?.radiusData,
    catchmentSettingsTemp?.placeCoordinates,
    catchmentSettingsTemp.datasetConfig?.datasets,
    catchmentSettingsTemp?.roadAccessConfig?.isochroneData,
    showAnalysisResultPopup,
    isDrawPolygon,
    isRankingLabelEnabled,
    analysisSettings?.max_catchments
  ]);

  return {
    handleAddIsochroneTemp,
    handleAddCatchmentLayer,
    handleAddRegionLayer,
    handleAddIsochroneLayer,
    removeCatchmentMapDisplay
  };
};

export default useCatchmentArea;
